import React, { useEffect, useState } from 'react'
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download } from 'lucide-react'

interface ImageLightboxProps {
 images: string[] | File[]
 currentIndex: number
 isOpen: boolean
 onClose: () => void
 onNavigate?: (index: number) => void
}

export const ImageLightbox: React.FC<ImageLightboxProps> = ({
 images,
 currentIndex,
 isOpen,
 onClose,
 onNavigate
}) => {
 const [zoom, setZoom] = useState(1)
 const [isDragging, setIsDragging] = useState(false)
 const [position, setPosition] = useState({ x: 0, y: 0 })
 const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

 useEffect(() => {
 if (isOpen) {
 // Bloqueia scroll do body quando aberto
 document.body.style.overflow = 'hidden'
 
 // Reseta zoom e posição quando abrir
 setZoom(1)
 setPosition({ x: 0, y: 0 })
 } else {
 document.body.style.overflow = 'unset'
 }

 return () => {
 document.body.style.overflow = 'unset'
 }
 }, [isOpen])

 // Atalhos de teclado
 useEffect(() => {
 if (!isOpen) return

 const handleKeyDown = (e: KeyboardEvent) => {
 if (e.key === 'Escape') {
 onClose()
 } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
 handlePrevious()
 } else if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
 handleNext()
 } else if (e.key === '+' || e.key === '=') {
 handleZoomIn()
 } else if (e.key === '-') {
 handleZoomOut()
 }
 }

 window.addEventListener('keydown', handleKeyDown)
 return () => window.removeEventListener('keydown', handleKeyDown)
 }, [isOpen, currentIndex, images.length])

 const handlePrevious = () => {
 if (currentIndex > 0 && onNavigate) {
 onNavigate(currentIndex - 1)
 setZoom(1)
 setPosition({ x: 0, y: 0 })
 }
 }

 const handleNext = () => {
 if (currentIndex < images.length - 1 && onNavigate) {
 onNavigate(currentIndex + 1)
 setZoom(1)
 setPosition({ x: 0, y: 0 })
 }
 }

 const handleZoomIn = () => {
 setZoom(prev => Math.min(prev + 0.5, 3))
 }

 const handleZoomOut = () => {
 setZoom(prev => Math.max(prev - 0.5, 0.5))
 }

 const handleDownload = () => {
 const currentImage = images[currentIndex]
 if (currentImage instanceof File) {
 const url = URL.createObjectURL(currentImage)
 const a = document.createElement('a')
 a.href = url
 a.download = currentImage.name
 a.click()
 URL.revokeObjectURL(url)
 } else if (typeof currentImage === 'string') {
 const a = document.createElement('a')
 a.href = currentImage
 a.download = `image-${currentIndex + 1}.jpg`
 a.target = '_blank'
 a.click()
 }
 }

 const getImageUrl = (image: string | File): string => {
 if (image instanceof File) {
 return URL.createObjectURL(image)
 }
 return image
 }

 const handleMouseDown = (e: React.MouseEvent) => {
 if (zoom > 1) {
 setIsDragging(true)
 setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
 }
 }

 const handleMouseMove = (e: React.MouseEvent) => {
 if (isDragging && zoom > 1) {
 setPosition({
 x: e.clientX - dragStart.x,
 y: e.clientY - dragStart.y
 })
 }
 }

 const handleMouseUp = () => {
 setIsDragging(false)
 }

 if (!isOpen || images.length === 0) return null

 const currentImage = images[currentIndex]

 return (
 <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm">
 {/* Header com controles */}
 <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
 <div className="max-w-7xl mx-auto flex items-center justify-between">
 {/* Contador */}
 <div className="text-white font-medium">
 {currentIndex + 1} / {images.length}
 </div>

 {/* Controles */}
 <div className="flex items-center space-x-2">
 {/* Zoom Out */}
 <button
 onClick={handleZoomOut}
 disabled={zoom <= 0.5}
 className="p-2 text-white hover:bg-card/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
 title="Diminuir zoom (-)"
 >
 <ZoomOut className="h-5 w-5" />
 </button>

 {/* Indicador de Zoom */}
 <span className="text-white text-sm font-medium min-w-[4rem] text-center">
 {Math.round(zoom * 100)}%
 </span>

 {/* Zoom In */}
 <button
 onClick={handleZoomIn}
 disabled={zoom >= 3}
 className="p-2 text-white hover:bg-card/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
 title="Aumentar zoom (+)"
 >
 <ZoomIn className="h-5 w-5" />
 </button>

 {/* Download */}
 <button
 onClick={handleDownload}
 className="p-2 text-white hover:bg-card/20 rounded-lg transition-colors"
 title="Baixar imagem"
 >
 <Download className="h-5 w-5" />
 </button>

 {/* Fechar */}
 <button
 onClick={onClose}
 className="p-2 text-white hover:bg-card/20 rounded-lg transition-colors"
 title="Fechar (ESC)"
 >
 <X className="h-5 w-5" />
 </button>
 </div>
 </div>
 </div>

 {/* Área da imagem */}
 <div
 className="absolute inset-0 flex items-center justify-center overflow-hidden"
 onMouseDown={handleMouseDown}
 onMouseMove={handleMouseMove}
 onMouseUp={handleMouseUp}
 onMouseLeave={handleMouseUp}
 style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
 >
 <img
 src={getImageUrl(currentImage)}
 alt={`Imagem ${currentIndex + 1}`}
 className="max-w-full max-h-full object-contain select-none transition-transform duration-200"
 style={{
 transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
 transformOrigin: 'center'
 }}
 draggable={false}
 />
 </div>

 {/* Navegação - Anterior */}
 {currentIndex > 0 && (
 <button
 onClick={handlePrevious}
 className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors backdrop-blur-sm"
 title="Imagem anterior (←)"
 >
 <ChevronLeft className="h-6 w-6" />
 </button>
 )}

 {/* Navegação - Próxima */}
 {currentIndex < images.length - 1 && (
 <button
 onClick={handleNext}
 className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors backdrop-blur-sm"
 title="Próxima imagem (→)"
 >
 <ChevronRight className="h-6 w-6" />
 </button>
 )}

 {/* Miniaturas */}
 {images.length > 1 && (
 <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-4">
 <div className="max-w-7xl mx-auto overflow-x-auto">
 <div className="flex space-x-2 justify-center">
 {images.map((image, index) => (
 <button
 key={index}
 onClick={() => {
 if (onNavigate) {
 onNavigate(index)
 setZoom(1)
 setPosition({ x: 0, y: 0 })
 }
 }}
 className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
 index === currentIndex
 ? 'border-white scale-110 shadow-lg'
 : 'border-white/30 hover:border-white/60 opacity-70 hover:opacity-100'
 }`}
 >
 <img
 src={getImageUrl(image)}
 alt={`Miniatura ${index + 1}`}
 className="w-full h-full object-cover"
 />
 </button>
 ))}
 </div>
 </div>
 </div>
 )}

 {/* Instruções */}
 <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/60 text-xs text-center space-y-1 pointer-events-none">
 <p>Use as setas ← → para navegar | ESC para fechar</p>
 <p>+ / - para zoom | Arraste para mover (quando com zoom)</p>
 </div>
 </div>
 )
}

